import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, CreditCard, LockKeyhole, Settings } from "lucide-react";
import { toast } from "sonner";
import { adminAPI } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  TailAdminDetailRow,
  TailAdminEmptyState,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const formatNaira = (value) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const AdminOperations = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paystack, setPaystack] = useState({ mode: "test", secret_key: "", public_key: "", current_password: "", reason: "", confirmation: "" });
  const [smtp, setSmtp] = useState({ host: "smtp.gmail.com", port: 587, username: "", password: "", from_email: "", timeout_seconds: 15, current_password: "", reason: "", confirmation: "" });
  const [activation, setActivation] = useState({ action: "select", config_id: "", confirmation: "", current_password: "", reason: "" });

  useEffect(() => {
    adminAPI.getOperationsConfig()
      .then((operations) => setConfig(operations.data))
      .catch(() => toast.error("Failed to load operations configuration"))
      .finally(() => setLoading(false));
    adminAPI.getProviderSettings()
      .then((response) => setProviders(response.data))
      .catch((error) => {
        if (error.response?.status !== 403) toast.error("Failed to load provider settings");
      });
  }, []);

  const savePaystack = async (event) => {
    event.preventDefault(); setSaving(true);
    try {
      await adminAPI.updatePaystackSettings(paystack);
      toast.success("Paystack rotation staged for a controlled restart");
      setPaystack((value) => ({ ...value, secret_key: "", public_key: "", current_password: "", confirmation: "" }));
      setProviders((await adminAPI.getProviderSettings()).data);
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to stage Paystack settings"); }
    finally { setSaving(false); }
  };

  const saveSmtp = async (event) => {
    event.preventDefault(); setSaving(true);
    try {
      await adminAPI.updateSmtpSettings(smtp);
      toast.success("SMTP rotation staged for a controlled restart");
      setSmtp((value) => ({ ...value, password: "", current_password: "", confirmation: "" }));
      setProviders((await adminAPI.getProviderSettings()).data);
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to stage SMTP settings"); }
    finally { setSaving(false); }
  };

  const activateVersion = async (event) => {
    event.preventDefault(); setSaving(true);
    try {
      if (activation.action === "acknowledge") {
        await adminAPI.acknowledgeProviderRollout(activation.config_id, activation);
        toast.success("Verified rollout acknowledged and version marked active");
      } else {
        await adminAPI.activateProviderSettings(activation.config_id, activation);
        toast.success("Version selected; perform and verify the controlled restart next");
      }
      setActivation({ action: "select", config_id: "", confirmation: "", current_password: "", reason: "" });
      setProviders((await adminAPI.getProviderSettings()).data);
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to activate configuration"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Platform controls"
        title="Operations"
        description="Review payment readiness and the escrow activation policy without exposing provider credentials."
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <TailAdminPanel title="Payment provider" description="Deployment-selected Paystack mode and key readiness.">
          {loading ? (
            <TailAdminEmptyState icon={CreditCard} title="Loading payment status" description="Reading the current deployment configuration." />
          ) : config ? (
            <div className="space-y-3">
              <TailAdminDetailRow
                label="Mode"
                value={<TailAdminStatusBadge tone={config.payment_mode === "live" ? "warning" : "neutral"}>{config.payment_mode}</TailAdminStatusBadge>}
                helper="Changing payment mode requires a controlled deployment."
              />
              <TailAdminDetailRow
                label="Credentials"
                value={config.paystack_configured ? "Configured" : "Not configured"}
                helper="Secret values are never returned to this portal."
              />
              <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0" />
                <p>Live-money activation is intentionally not available as a browser toggle. It must pass callback, webhook, reconciliation, and rollback checks.</p>
              </div>
            </div>
          ) : null}
        </TailAdminPanel>

        <TailAdminPanel title="Escrow activation" description="Current rule applied when a campaign escrow is created.">
          {config?.escrow_policy ? (
            <div className="space-y-3">
              <TailAdminDetailRow label="Minimum conversions" value={config.escrow_policy.minimum_conversions} />
              <TailAdminDetailRow label="Minimum floor" value={formatNaira(config.escrow_policy.minimum_floor_ngn)} />
              <TailAdminDetailRow label="Low-balance threshold" value={`${config.escrow_policy.low_balance_multiplier} × commission`} />
              <TailAdminDetailRow label="Calculation" value={config.escrow_policy.calculation} />
              <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                {config.escrow_policy.editable ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />}
                <p>Editing is not enabled on this foundation screen. A safe editor needs an explicit rule definition, role restriction, audit history, effective date, and validation against already-active campaigns.</p>
              </div>
            </div>
          ) : (
            <TailAdminEmptyState icon={Settings} title="Escrow policy unavailable" description="The current policy could not be loaded." />
          )}
        </TailAdminPanel>
      </div>

      {providers ? <div className="grid gap-6 xl:grid-cols-2">
        <TailAdminPanel title="Rotate Paystack credentials" description="Secrets are write-only. Test and live credentials are stored separately; this screen cannot switch payment mode.">
          <form className="space-y-4" onSubmit={savePaystack}>
            <div><Label htmlFor="paystack-mode">Credential set</Label><select id="paystack-mode" className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3" value={paystack.mode} onChange={(e) => setPaystack({ ...paystack, mode: e.target.value, confirmation: "" })}><option value="test">Test</option><option value="live">Live</option></select></div>
            <div><Label htmlFor="paystack-secret">Secret key</Label><Input id="paystack-secret" type="password" autoComplete="new-password" value={paystack.secret_key} onChange={(e) => setPaystack({ ...paystack, secret_key: e.target.value })} required /><p className="mt-1 text-xs text-gray-500">Active: {providers?.paystack?.[paystack.mode]?.active?.masked?.secret_key || "deployment setting or not configured"}</p></div>
            <div><Label htmlFor="paystack-public">Public key</Label><Input id="paystack-public" type="password" autoComplete="off" value={paystack.public_key} onChange={(e) => setPaystack({ ...paystack, public_key: e.target.value })} required /><p className="mt-1 text-xs text-gray-500">Active: {providers?.paystack?.[paystack.mode]?.active?.masked?.public_key || "deployment setting or not configured"}</p></div>
            <div><Label htmlFor="paystack-reason">Reason</Label><Input id="paystack-reason" value={paystack.reason} onChange={(e) => setPaystack({ ...paystack, reason: e.target.value })} minLength={10} required /></div>
            <div><Label htmlFor="paystack-password">Current admin password</Label><Input id="paystack-password" type="password" autoComplete="current-password" value={paystack.current_password} onChange={(e) => setPaystack({ ...paystack, current_password: e.target.value })} required /></div>
            <div><Label htmlFor="paystack-confirm">Type ROTATE PAYSTACK {paystack.mode.toUpperCase()}</Label><Input id="paystack-confirm" value={paystack.confirmation} onChange={(e) => setPaystack({ ...paystack, confirmation: e.target.value })} required /></div>
            <Button type="submit" disabled={saving}>Stage credential rotation</Button>
          </form>
        </TailAdminPanel>

        <TailAdminPanel title="Rotate SMTP settings" description="The deployment allowlist restricts the SMTP host. Passwords are write-only and never returned.">
          <form className="space-y-4" onSubmit={saveSmtp}>
            <div className="grid gap-4 sm:grid-cols-2"><div><Label htmlFor="smtp-host">Host</Label><Input id="smtp-host" value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} required /></div><div><Label htmlFor="smtp-port">Port</Label><Input id="smtp-port" type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) })} required /></div></div>
            <div><Label htmlFor="smtp-username">Username</Label><Input id="smtp-username" value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} required /></div>
            <div><Label htmlFor="smtp-from">From email</Label><Input id="smtp-from" type="email" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} required /></div>
            <div><Label htmlFor="smtp-secret">App password</Label><Input id="smtp-secret" type="password" autoComplete="new-password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} required /><p className="mt-1 text-xs text-gray-500">Active: {providers?.smtp?.active?.masked?.password || "deployment setting or not configured"}</p></div>
            <div><Label htmlFor="smtp-reason">Reason</Label><Input id="smtp-reason" value={smtp.reason} onChange={(e) => setSmtp({ ...smtp, reason: e.target.value })} minLength={10} required /></div>
            <div><Label htmlFor="smtp-password">Current admin password</Label><Input id="smtp-password" type="password" autoComplete="current-password" value={smtp.current_password} onChange={(e) => setSmtp({ ...smtp, current_password: e.target.value })} required /></div>
            <div><Label htmlFor="smtp-confirm">Type ROTATE SMTP</Label><Input id="smtp-confirm" value={smtp.confirmation} onChange={(e) => setSmtp({ ...smtp, confirmation: e.target.value })} required /></div>
            <Button type="submit" disabled={saving}>Stage SMTP rotation</Button>
          </form>
        </TailAdminPanel>
      </div> : null}

      {providers ? <TailAdminPanel title="Provider rollout" description="First select a version, then restart and verify every replica. Only after verification should the rollout be acknowledged as active.">
        <form className="grid gap-4 lg:grid-cols-2" onSubmit={activateVersion}>
          <div><Label htmlFor="activate-action">Action</Label><select id="activate-action" className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3" value={activation.action} onChange={(e) => setActivation({ ...activation, action: e.target.value, confirmation: "" })}><option value="select">Select for rollout</option><option value="acknowledge">Acknowledge verified rollout</option></select></div>
          <div><Label htmlFor="activate-id">Configuration version ID</Label><Input id="activate-id" value={activation.config_id} onChange={(e) => setActivation({ ...activation, config_id: e.target.value })} placeholder={providers?.smtp?.staged?.config_id || providers?.paystack?.test?.staged?.config_id || "apc_…"} required /></div>
          <div><Label htmlFor="activate-confirm">Confirmation phrase</Label><Input id="activate-confirm" value={activation.confirmation} onChange={(e) => setActivation({ ...activation, confirmation: e.target.value })} placeholder={`${activation.action === "acknowledge" ? "ACKNOWLEDGE" : "ACTIVATE"} PAYSTACK TEST or SMTP ADMIN-EMAIL`} required /></div>
          <div><Label htmlFor="activate-reason">Reason</Label><Input id="activate-reason" value={activation.reason} onChange={(e) => setActivation({ ...activation, reason: e.target.value })} minLength={10} required /></div>
          <div><Label htmlFor="activate-password">Current admin password</Label><Input id="activate-password" type="password" autoComplete="current-password" value={activation.current_password} onChange={(e) => setActivation({ ...activation, current_password: e.target.value })} required /></div>
          <Button type="submit" disabled={saving}>{activation.action === "acknowledge" ? "Acknowledge verified rollout" : "Select for controlled restart"}</Button>
        </form>
      </TailAdminPanel> : null}

      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        Staged changes activate only after the operator performs a controlled restart. This portal cannot write Kubernetes or cloud secrets, restart services, enable email delivery, or switch Paystack into live mode.
      </div>
    </div>
  );
};

export default AdminOperations;
